/**
 * Fixed Bar - Mobile sticky add to bag button for PDP
 *
 * Shows when the add to cart button scrolls out of view
 */

class ProductFixedBar extends window.BAO.CustomElement() {
  static get requiredExternalElements () {
    return ['pdp-add-to-cart-button']
  }

  connectedCallback () {
    super.connectedCallback()
    this.setupObserver()
  }

  setupObserver () {
    const targetEl = this.externalEls['pdp-add-to-cart-button']?.element
    if (!targetEl) return

    // Ensure bar is always hidden on load before the observer's first async callback
    this.isVisible = false

    // Observer to watch when the add to cart button scrolls out of view
    this.observer = new IntersectionObserver(
      ([entry]) => {
        const ctaIsAboveViewport =
          !entry.isIntersecting && entry.boundingClientRect.top < 0

        this.isVisible = ctaIsAboveViewport
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      },
    )

    this.observer.observe(targetEl)
  }

  setupListeners () {
    super.setupListeners()

    const button = this.els?.button?.element
    if (!button) return

    const formId = button.getAttribute('form')
    if (!formId) return

    // Sold-out variants must remain disabled so they are not clickable.
    if (
      button.hasAttribute('disabled') &&
      button.hasAttribute('data-selection-required')
    ) {
      button.removeAttribute('disabled')
      button.setAttribute('aria-disabled', 'true')
    }

    this.variantAvailable =
      !button.hasAttribute('data-selection-required') &&
      !button.hasAttribute('disabled')

    this.onVariantChange = this.onVariantChange.bind(this)

    // Listen to variant change events
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.VARIANT.CHANGE,
      this.onVariantChange,
    )

    // Click listener for scroll behavior when no variant selected
    this.listeners.add(button, 'click', e => {
      // If variant is selected, let the form submit normally
      if (this.variantAvailable) return

      // No variant selected - prevent submission and scroll to form
      e.preventDefault()
      e.stopPropagation()

      const variants = this.externalEls.variants?.element
      if (!variants) return

      const headerOffset = this.externalEls.header?.element
        ? this.externalEls.header.element.offsetHeight
        : 0
      const elementPosition = variants.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    })

    this.setupDrawerObserver()
  }

  setupDrawerObserver () {
    const fixedBarEl = this.els['fixed-bar']?.element
    const cartDrawer = this.externalEls['cart-drawer']?.element

    if (!cartDrawer || !fixedBarEl) return

    // Sync initial state immediately in case drawer is already open on attach
    const syncDrawerState = () => {
      const isOpen = cartDrawer.classList.contains('drw-Drawer-active')
      fixedBarEl.setAttribute('data-drawer-open', isOpen ? 'true' : 'false')
    }

    this.drawerObserver = new MutationObserver(syncDrawerState)

    this.drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class'],
    })

    syncDrawerState()
  }

  onVariantChange (e) {
    const variant = e.detail?.variant
    const isAvailable = variant?.data?.available === true
    const hasId = variant?.data?.id !== undefined

    // Update state
    this.variantAvailable = isAvailable && hasId
    // Update button state based on variant availability
    const button = this.els?.button?.element
    if (!button) return

    if (isAvailable && hasId) {
      // Variant selected and available — fully enable
      button.removeAttribute('disabled')
      button.removeAttribute('aria-disabled')
      button.removeAttribute('data-selection-required')
    } else if (hasId) {
      // Variant selected but sold out — truly disable, not clickable
      button.setAttribute('disabled', '')
      button.removeAttribute('aria-disabled')
      button.removeAttribute('data-selection-required')
    } else {
      // No variant selected — aria-disabled so scroll-to-form click still works
      button.removeAttribute('disabled')
      button.setAttribute('aria-disabled', 'true')
      button.setAttribute('data-selection-required', '')
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    if (this.observer) {
      this.observer.disconnect()
    }

    if (this.drawerObserver) {
      this.drawerObserver.disconnect()
    }

    // Clean up variant change listener
    if (this.onVariantChange) {
      window.BAO.eventBus.removeEventListener(
        window.BAO.EVENTS.VARIANT.CHANGE,
        this.onVariantChange,
      )
    }

    this.isVisible = false
  }

  get isVisible () {
    return document.body.classList.contains(this.visibleClass)
  }

  set isVisible (val) {
    val
      ? document.body.classList.add(this.visibleClass)
      : document.body.classList.remove(this.visibleClass)
  }

  get visibleClass () {
    return this.dataset.visibleClass || 'is-FixedBar-visible'
  }

  getScrollOffset () {
    if (this.dataset.scrollOffset) {
      return parseInt(this.dataset.scrollOffset, 10)
    }

    const header = this.externalEls?.header?.element
    return header ? header.offsetHeight : 0
  }
}

if (!customElements.get('product-fixed-bar')) {
  customElements.define('product-fixed-bar', ProductFixedBar)
}
